import { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { DatasetStatusEnum, EDatasetStatus } from "types";
import {
  startCreationStatusPolling,
  stopCreationStatusPolling,
  setCreationStatus,
} from "redux/CreateDataset/actions";
import {
  fetchFilterConfig,
  fetchForAsyncDataPage,
} from "redux/SingleDataset/actions";
import { getDataset } from "redux/SingleDataset/selectors";
import { getCreationStatus } from "redux/CreateDataset/selectors";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import VLActionModal from "views/uikit/VLModal/VLActionModal";
import styles from "./style.module.scss";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActiveQueryByOperationId } from "vl-api/vlUtils";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { NEW_STATUS } from "../../../helpers/constants/filters";
import { triggerUserConfigRefetch } from "../../../contexts/UserConfigContext";

function AddMediaCompletedModal() {
  const { datasetId } = useParams();
  const dispatch = useDispatch();
  const dataset = useSelector(getDataset);
  const creationStatus = useSelector(getCreationStatus);
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const wasUpdatingRef = useRef(false);
  const displayLPText = useFeatureFlagService("FLYWHEEL").show;
  const queryParamsFilters = useAllQueryParamsFilters();
  const queryClient = useQueryClient();
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;

  useEffect(() => {
    const shouldStartPolling = displayNewStatus
      ? dataset?.is_updating || dataset?.status_new === EDatasetStatus.READ_ONLY
      : dataset?.status === DatasetStatusEnum.UPDATING;
    if (shouldStartPolling) {
      wasUpdatingRef.current = true;

      if (datasetId) {
        dispatch(startCreationStatusPolling(datasetId) as any);
      }
    }

    return () => {
      dispatch(stopCreationStatusPolling() as any);
    };
  }, [
    dataset?.is_updating,
    dataset?.status,
    dispatch,
    datasetId,
    displayNewStatus,
    dataset?.status_new,
  ]);

  useEffect(() => {
    const isReady = displayNewStatus
      ? creationStatus?.status_new === EDatasetStatus.READY
      : creationStatus?.status === "READY";

    const isPendingIndex = displayNewStatus
      ? creationStatus?.status_new === EDatasetStatus.PARTIAL_INDEX
      : creationStatus?.status === "PENDING_INDEX";

    if (wasUpdatingRef.current && (isReady || isPendingIndex)) {
      if (isPendingIndex) {
        setModalContent(
          <>
            New data was added to your dataset and is ready to explore. <br />
            To use features like{" "}
            {displayLPText ? "label propagation" : "enrichment"}, you'll need to
            re-index the dataset.
            <br />
            you can do it now or wait if more data is on the way.
          </>,
        );
      } else if (isReady && creationStatus?.taskType === "SNAPSHOT_RESTORE") {
        setModalContent("Snapshot was restored successfully");
        invalidateActiveQueryByOperationId(
          queryClient,
          "listSnapshotsApiV1DatasetDatasetIdSnapshotGet",
        );
      }
      wasUpdatingRef.current = false;
      if (datasetId) {
        dispatch(fetchForAsyncDataPage(datasetId, queryParamsFilters) as any);
        dispatch(setCreationStatus(null));
        triggerUserConfigRefetch();
        dispatch(fetchFilterConfig(datasetId) as any);
      }
    }
  }, [
    creationStatus,
    datasetId,
    dispatch,
    queryParamsFilters,
    displayLPText,
    queryClient,
    displayNewStatus,
  ]);

  const handleClose = () => {
    setModalContent(null);
  };

  return (
    <VLActionModal
      actionLabel={"OK"}
      onClick={handleClose}
      isOpen={Boolean(modalContent)}
      variant="success"
      headers={{
        title: "Good News!",
      }}
      onClose={handleClose}
    >
      <div className={styles.content}>{modalContent}</div>
    </VLActionModal>
  );
}

export default AddMediaCompletedModal;
